import { getTenantId, setLocalTenantId } from "@/lib/storeManager";
import { createContext, ReactNode, useEffect, useState } from "react";

interface TenantContextType {
  tenantId: string | null;
  setTenantId: (id: string) => void;
  clearTenantId: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

/** Frontend design mode: no real tenant required */
const DESIGN_TENANT_ID = "design";

const RESERVED_PATHS = new Set([
  "sd",
  "login",
  "signup",
  "forgot-password",
  "forgot-password-reset",
  "tenant",
  "schemas",
  "module",
  "workflow",
  "imports",
  "print",
  "nps",
  "notification",
  "translations",
  "print-format",
  "letter-head",
  "email-configuration",
  "sms-configuration",
  "social-configuration",
  "tenant-profile",
  "sla-configuration",
  "sla-configurations",
  "route-details",
]);

const getPathTenantCandidate = () => {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const candidate = pathParts[0] ?? null;
  if (!candidate || RESERVED_PATHS.has(candidate)) {
    return null;
  }
  return candidate;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    const storedTenantId = getTenantId();
    if (storedTenantId) {
      return storedTenantId;
    }

    return getPathTenantCandidate() ?? DESIGN_TENANT_ID;
  });

  const setTenantIdHandler = (id: string) => {
    setLocalTenantId(id);
    setTenantIdState(id);
  };

  const clearTenantId = () => {
    localStorage.removeItem("tenantId");
    setTenantIdState(DESIGN_TENANT_ID);
  };

  useEffect(() => {
    const handleUrlChange = () => {
      const urlTenantId = getPathTenantCandidate();

      if (
        urlTenantId &&
        urlTenantId !== "undefined" &&
        urlTenantId !== "null"
      ) {
        setTenantIdHandler(urlTenantId);
      }
    };

    window.addEventListener("popstate", handleUrlChange);
    handleUrlChange();

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        setTenantId: setTenantIdHandler,
        clearTenantId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export { TenantContext };
